'use client';
import { CloseBigIcon, DetailsIcon, VolumeOffIcon, VolumeOnIcon } from '@/components/icons/svg-icons';
import { Button } from '@nf/ui';
import { useEffect, useRef, useState } from 'react';
import videojs from 'video.js';
import { StreamingUnactivated } from '../streaming-item/streaming-unactived';
import { useStreamingStore } from '@/store/streaming';
import { VideoDeviceType } from '@/services/streaming.service';
import { useAppearanceStore } from '@/store/appearance';
import { ViewEnum } from '@/types';
import clsx from 'clsx';
import { miniStreamingStyle } from '@/features/odds-table-mars/components/mini-streaming/mini-streaming.style';
import { css } from 'styled-system/css';
import { useTranslations } from 'next-intl';
import { useSingleMatchFilter } from '@/features/single-match/bet-types-section/contexts/single-match-filter-context';

export const VideoBase = ({ device }: { device?: VideoDeviceType; }) => {
	const view = useAppearanceStore(state => state.view);
	const t = useTranslations('General');
	const videoRef = useRef<any>(null);
	const playerRef = useRef<any>(null);
	const { isMute, setIsMute } = useSingleMatchFilter(); // 使用 true/false 來代表音量是否開啟
	const [isError, setIsError] = useState(false); // 是否錯誤
	const [isShowControl, setIsShowControl] = useState(false);
	const [isShowDetail, setIsShowDetail] = useState(false);
	const [videoErrotype, setVideoErrotype] = useState('');
	const [streamingUrl, isRefresh, isStreamingFloat, setIsStreamingFloat, setStreamingType, isLoading] = useStreamingStore(
		state => [state.streamingUrl, state.isRefresh, state.isStreamingFloat, state.setIsStreamingFloat, state.setStreamingType, state.isLoading]
	);

	const switchVoice = () => {
		setIsMute(!isMute);
	};

	const toggleShowControl = () => {
		setIsShowControl((isShowControl) => !isShowControl);
	}

	useEffect(() => {
		const player = playerRef.current;
		if (isRefresh && player && !player.isDisposed()) {
			playerRef.current.dispose();
			playerRef.current = null;
		}

		if (!playerRef.current) {
			setIsError(false);
			const placeholderEl = videoRef.current;
			if (!placeholderEl) return;
			const videoElement = placeholderEl.appendChild(document.createElement('video-js'));
			videoElement.style = 'object-fit:fill;width:100%;height:100%;';
			videoElement.playsInline = 'true';
			const player = playerRef.current = videojs(videoElement, {
				autoplay: false,
				loop: true,
				muted: isMute,
				controls: false
			});
			player.src({ type: 'application/x-mpegURL', src: streamingUrl });
			player.ready(() => {
				player.tech().on('retryplaylist', () => {
					setIsError(!isError);
					setVideoErrotype('5');
					if (player && !player.isDisposed()) {
						player.dispose();
						playerRef.current = null;
					}
				});

				player.on('error', (function () {
					setIsError(!isError);
					setVideoErrotype('1');
				}));
			});
		} else {
			const player = playerRef.current;
			player.muted(isMute);
			player.autoplay(false);
			player.src({ type: 'application/x-mpegURL', src: streamingUrl });
		}

		return () => {
			if (player && !player.isDisposed()) {
				player.src({});
				player.dispose();
				playerRef.current = null;
			}
		};
	}, [streamingUrl, isMute, isRefresh, videoRef, isLoading]);

	useEffect(() => {
		const player = playerRef.current;
		const mediaQueryDesktop = window.matchMedia('(min-width: 769px)');
		const mediaQueryMobile = window.matchMedia('(min-width: 577px)');
		if (player && window.innerWidth > 768 && device === 'desktop') {
			player.play();
		} else if (player && window.innerWidth < 768 && device === 'mobile') {
			player.play();
		} else {
			if (player) {
				player.play();
			}
		}
		
		const handleMediaChange = () => {
			const videoTag = document.getElementsByTagName('video');
			if (videoTag && videoTag.length > 1) {
				if (window.innerWidth > 768) {
					videoTag[0].pause() !== undefined ? videoTag[0].pause() : null;
					videoTag[1].play() !== undefined ? videoTag[1].play() : null;
				} else {
					videoTag[0].play() !== undefined ? videoTag[0].play() : null;
					videoTag[1].pause() !== undefined ? videoTag[1].pause() : null;
				}
			}
		};
		mediaQueryDesktop.addEventListener('change', handleMediaChange);
		mediaQueryMobile.addEventListener('change', handleMediaChange);
		return () => {
			mediaQueryDesktop.removeEventListener('change', handleMediaChange);
			mediaQueryMobile.removeEventListener('change', handleMediaChange);
		};
	}, [isRefresh, isMute]);

	const handleStreamingClose = () => {
		setIsStreamingFloat(false);
		setStreamingType('');
	};

	return (
		<>
			{isError ?
				(
					<StreamingUnactivated type={videoErrotype} />
				) : (
					view === ViewEnum.mars ? (
						<div className="video-view video-view-ref">
							{isStreamingFloat ?
								<Button className="btn--icon btn--close" onClick={handleStreamingClose}>
									<CloseBigIcon />
								</Button> : null
							}

							<div className={miniStreamingStyle.view}>
								<div
									style={{
										objectFit: 'fill',
										// width: '375px',
										// height: '210px'
									}}
									className="video-js"
									ref={videoRef}
									onClick={toggleShowControl}
								/>
							</div>
							<div className={css({ display: isShowControl ? 'block' : 'none' })}>
								<div className={miniStreamingStyle.tools}>
									<Button variant="icon" onClick={() => setIsShowDetail(true)}>
										<DetailsIcon />
									</Button>
									<Button
										variant="icon"
										onClick={switchVoice}
									>
										{!isMute ? <VolumeOnIcon /> : <VolumeOffIcon />}
									</Button>
								</div>
								<div
									className={clsx(
										'scrollbar',
										css(miniStreamingStyle.videoInfo, isShowDetail && miniStreamingStyle.videoInfoActive)
									)}
									onClick={() => setIsShowDetail(false)}
								>
									<p>{t('lbl_Disclaimer')}</p>
								</div>
							</div>
						</div>
					) : (
						<div className="video-view video-view-ref">
							{isStreamingFloat ?
								<Button className="btn--icon btn--close" onClick={handleStreamingClose}>
									<CloseBigIcon />
								</Button> : null}
							<div
								style={{ objectFit: 'fill' }}
								className="video-js"
								ref={videoRef}
							/>
							<div className="video-overlay">
								<div className="video-control">
									<Button variant="icon" onClick={switchVoice} className="vjs-mute-control vjs-control vjs-button">
										{!isMute ? <VolumeOnIcon /> : <VolumeOffIcon />} {/* 根據音量狀態顯示不同的圖示 */}
									</Button>
								</div>
							</div>
						</div>
					)
				)}
		</>
	);
};
